import { Injectable, signal } from '@angular/core';
import { forkJoin, Observable } from 'rxjs';
import { OrderEnumsProxyService } from '../proxies/order/order-enums.proxy.service';
import { ProductionEnumsProxyService } from '../proxies/production/production-enums.proxy.service';
import { InventoryEnumsProxyService } from '../proxies/inventory/inventory-enums.proxy.service';
import { ItemEnumsProxyService } from '../proxies/item/item-enums.proxy.service';
import { LocalHelperService } from './local-helper.service';
import _ from 'lodash';
import { EnumObjectExtended } from '@card-software/library';

@Injectable({
  providedIn: 'root',
})
export class EnumService {
  private readonly orderEnumLocalStorageKey = 'orderEnums';
  private readonly productionEnumLocalStorageKey = 'productionEnums';
  private readonly inventoryEnumLocalStorageKey = 'inventoryEnums';
  private readonly itemEnumLocalStorageKey = 'itemEnums';
  private isLoadingEnums = false;
  public readonly enumsVersion = signal(0);

  constructor(
    private readonly orderEnumsProxyService: OrderEnumsProxyService,
    private readonly productionEnumsProxyService: ProductionEnumsProxyService,
    private readonly inventoryEnumsProxyService: InventoryEnumsProxyService,
    private readonly itemEnumsProxyService: ItemEnumsProxyService,
    private readonly localHelperService: LocalHelperService,
  ) {}

  public loadEnums(): Observable<boolean> {
    localStorage.removeItem(this.orderEnumLocalStorageKey);
    localStorage.removeItem(this.productionEnumLocalStorageKey);
    localStorage.removeItem(this.inventoryEnumLocalStorageKey);
    localStorage.removeItem(this.itemEnumLocalStorageKey);
    this.isLoadingEnums = true;
    return new Observable<boolean>((observer) => {
      forkJoin([
        this.orderEnumsProxyService.getAll(),
        this.productionEnumsProxyService.getAll(),
        this.inventoryEnumsProxyService.getAll(),
        this.itemEnumsProxyService.getAll(),
      ]).subscribe({
        next: (response) => {
          localStorage.setItem(this.orderEnumLocalStorageKey, JSON.stringify(response[0]));
          localStorage.setItem(this.productionEnumLocalStorageKey, JSON.stringify(response[1]));
          localStorage.setItem(this.inventoryEnumLocalStorageKey, JSON.stringify(response[2]));
          localStorage.setItem(this.itemEnumLocalStorageKey, JSON.stringify(response[3]));
          this.isLoadingEnums = false;
          this.enumsVersion.update((v) => v + 1);
          observer.next(true);
          observer.complete();
        },
        error: (error) => {
          console.error('Error loading enums', error);
          this.isLoadingEnums = false;
          observer.next(false);
          observer.complete();
        },
      });
    });
  }

  public getEnum(enumName: string, domain: 'production' | 'order' | 'inventory' | 'item'): EnumObjectExtended {
    let enumObj = null;

    switch (domain) {
      case 'production':
        enumObj = JSON.parse(localStorage.getItem(this.productionEnumLocalStorageKey)!);
        break;
      case 'order':
        enumObj = JSON.parse(localStorage.getItem(this.orderEnumLocalStorageKey)!);
        break;
      case 'inventory':
        enumObj = JSON.parse(localStorage.getItem(this.inventoryEnumLocalStorageKey)!);
        break;
      case 'item':
        enumObj = JSON.parse(localStorage.getItem(this.itemEnumLocalStorageKey)!);
        break;
    }

    if (!enumObj) {
      if (!this.isLoadingEnums) {
        this.loadEnums().subscribe({
          error: (error) => {
            console.error('Error loading enums', error);
          },
        });
      }
      return { name: '', values: {}, valuesEnglish: {} };
    }

    const obj = enumObj.find((x: { name: string; values: object }) => x.name === enumName);
    if (obj) {
      const translatedValues: Record<string, string> = {};
      const englishValues: Record<string, string> = {};

      for (const key in obj.values) {
        translatedValues[key] = _.startCase(this.localHelperService.getLocalizedValue(obj.values[key]));
      }

      if (obj.valuesEnglish) {
        for (const key in obj.valuesEnglish) {
          englishValues[key] = _.startCase(obj.valuesEnglish[key]);
        }
      } else {
        for (const key in obj.values) {
          const value = obj.values[key];
          englishValues[key] = _.startCase(typeof value === 'object' && value.en ? value.en : value);
        }
      }

      return { name: obj.name, values: translatedValues, valuesEnglish: englishValues };
    } else {
      return { name: enumName, values: {}, valuesEnglish: {} } as EnumObjectExtended;
    }
  }
}
